import React, { useEffect, useState } from 'react';
import { XIcon } from 'lucide-react';
interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  position?: 'right' | 'left';
  size?: 'sm' | 'md' | 'lg';
  fullHeight?: boolean;
  animation?: 'fade' | 'slide-in-right' | 'slide-in-left';
}
export function Drawer({
  isOpen,
  onClose,
  title,
  children,
  position = 'right',
  size = 'md',
  fullHeight = false,
  animation = 'fade'
}: DrawerProps) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      // Prevent body scrolling when drawer is open
      document.body.style.overflow = 'hidden';
    } else {
      // Re-enable scrolling when drawer is closed
      document.body.style.overflow = '';
      const timer = setTimeout(() => {
        setMounted(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);
  if (!mounted && !isOpen) return null;
  const sizeClasses = {
    sm: 'w-80',
    md: 'w-96',
    lg: 'w-1/3'
  };
  const positionClasses = {
    right: isOpen ? 'right-0' : '-right-full',
    left: isOpen ? 'left-0' : '-left-full'
  };
  const animationClasses = {
    fade: 'animate-fade',
    'slide-in-right': 'animate-slide-in-right',
    'slide-in-left': 'animate-slide-in-left'
  };
  return <>
      {/* Backdrop - transparent but clickable */}
      <div className={`fixed inset-0 bg-black bg-opacity-50 transition-opacity duration-300 z-40 ${isOpen ? 'pointer-events-auto' : 'opacity-0 pointer-events-none'}`} onClick={onClose} data-id="element-23" />
      {/* Drawer */}
      <div className={`fixed top-0 h-screen ${sizeClasses[size]} bg-[#0A0A0A] shadow-2xl z-50 transition-all duration-300 ease-in-out transform ${positionClasses[position]} ${isOpen && animationClasses[animation]} border-l border-[#222222]`} data-id="element-24">
        <div className="flex flex-col h-full" data-id="element-25">
          <div className="flex items-center justify-between p-4 border-b border-[#222222]" data-id="element-26">
            <h2 className="text-lg font-semibold text-white" data-id="element-27">{title}</h2>
            <button onClick={onClose} className="p-1 rounded-full text-gray-400 hover:text-white hover:bg-[#121212] transition-colors" data-id="element-28">
              <XIcon size={20} data-id="element-29" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 text-white" data-id="element-30">
            {children}
          </div>
        </div>
      </div>
      <style jsx global data-id="element-31">{`
        @keyframes fade {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes slide-in-right {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
        @keyframes slide-in-left {
          from {
            transform: translateX(-100%);
          }
          to {
            transform: translateX(0);
          }
        }
        .animate-fade {
          animation: fade 0.3s ease-out forwards;
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out forwards;
        }
        .animate-slide-in-left {
          animation: slide-in-left 0.3s ease-out forwards;
        }
      `}</style>
    </>;
}